import { useState } from 'react';
import { formatDateTime } from '@shared/format/datetime';
import { clsx } from 'clsx';
import { StickyNote, Pencil, Trash2, Loader2, Check, X, Bot, Plus } from 'lucide-react';
import { ConfirmDialog } from '@centraoneplus/ui';
import type { Message } from '@centraoneplus/shared-types';

/** Notes are internal conversation messages authored by the workflow engine. */
export function isAutomationNote(m: Pick<Message, 'senderName'>): boolean {
  return m.senderName === 'Automation';
}

/** Edit marker is stamped in message metadata (Message has no isEdited column). */
export function noteWasEdited(m: Pick<Message, 'metadata'>): boolean {
  const meta = m.metadata as Record<string, unknown> | undefined | null;
  return !!meta && (meta as Record<string, unknown>).edited === true;
}

/** Newest first. */
export function sortNotesNewestFirst(notes: Message[]): Message[] {
  return [...notes].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/** Reply-editor notes are HTML; render/edit them as plain text in this compact tab. */
function htmlToText(html: string): string {
  const normalized = (html || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n');
  if (typeof document === 'undefined') return normalized.replace(/<[^>]+>/g, '').trim();
  const div = document.createElement('div');
  div.innerHTML = normalized;
  return (div.textContent || '').trim();
}

interface Props {
  notes: Message[];
  onEdit: (messageId: string, content: string) => Promise<void>;
  onDelete: (messageId: string) => Promise<void>;
  /** Fired by the empty-state "Create a new note" button — the parent focuses
   *  the reply composer with internal-note mode enabled. */
  onCreateNote?: () => void;
}

/**
 * The ticket "Notes" tab — a focused view of the conversation's internal notes
 * (agent "Internal Note" replies + automation-created notes). Add is done via
 * the reply box's "Internal Note" toggle; here you can read, edit, and delete them.
 */
export function TicketNotesTab({ notes, onEdit, onDelete, onCreateNote }: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Id of the note pending delete — drives the in-app confirmation popup
  // (replaces the native window.confirm).
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const save = async (id: string) => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await onEdit(id, text.trim());
      setEditingId(null);
      setText('');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    setDeletingId(id);
    try {
      await onDelete(id);
      // Close the popup only on success; on failure the parent surfaces a toast
      // and the note stays in the list (the store only removes it on success).
      setConfirmDeleteId(null);
    } finally {
      setDeletingId(null);
    }
  };

  if (notes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-10 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-500 dark:bg-amber-900/20 dark:text-amber-400">
          <StickyNote className="h-6 w-6" />
        </div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">No notes yet</p>
        <p className="mt-1 mb-4 text-xs text-gray-500 dark:text-gray-400">
          Add an internal note visible only to your team.
        </p>
        {onCreateNote && (
          <button
            type="button"
            onClick={onCreateNote}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/30 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create a new note
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 p-4">
      {sortNotesNewestFirst(notes).map((n) => {
        const automation = isAutomationNote(n);
        const editing = editingId === n.id;
        return (
          <div
            key={n.id}
            className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/50 dark:bg-amber-900/15"
          >
            <div className="mb-1 flex items-center gap-1.5">
              {automation && <Bot className="h-3.5 w-3.5 text-purple-500" />}
              <span
                className={clsx(
                  'text-xs font-semibold',
                  automation ? 'text-purple-600 dark:text-purple-400' : 'text-gray-700 dark:text-gray-300',
                )}
              >
                {n.senderName || 'Agent'}
              </span>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {formatDateTime(n.createdAt)}
              </span>
              {noteWasEdited(n) && <span className="text-[10px] italic text-gray-400 dark:text-gray-500">edited</span>}
              {!editing && (
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    type="button"
                    title="Edit note"
                    onClick={() => { setEditingId(n.id); setText(htmlToText(n.content)); }}
                    className="p-1 rounded border border-gray-200 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    title="Delete note"
                    disabled={deletingId === n.id}
                    onClick={() => setConfirmDeleteId(n.id)}
                    className="p-1 rounded border border-gray-200 text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    {deletingId === n.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {editing ? (
              <div>
                <textarea
                  rows={3}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <div className="mt-1 flex justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => { setEditingId(null); setText(''); }}
                    className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                  >
                    <X className="h-3 w-3" /> Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!text.trim() || saving}
                    onClick={() => save(n.id)}
                    className="inline-flex items-center gap-1 rounded bg-primary-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
                  </button>
                </div>
              </div>
            ) : (
              <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">{htmlToText(n.content)}</p>
            )}
          </div>
        );
      })}

      <ConfirmDialog
        isOpen={confirmDeleteId !== null}
        loading={deletingId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => { if (confirmDeleteId) remove(confirmDeleteId); }}
        title="Delete note"
        message="Delete this note? This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
      />
    </div>
  );
}

export default TicketNotesTab;